import { useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  CheckCircle2,
  Clipboard,
  ExternalLink,
  Radar,
  RefreshCw,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Layout } from '@/components/layout/Layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import {
  revealAcquisitionSignal,
  useAcquisitionSignals,
  useUpdateAcquisitionSignal,
} from '@/hooks/use-acquisition-signals';
import type {
  AcquisitionManualOutreachEligibility,
  AcquisitionOperatorSignalWithEligibility,
  AcquisitionOperatorSource,
  AcquisitionOperatorStatus,
} from '@/lib/marketing/operator-queue';
import { cn } from '@/lib/utils';

type StatusFilter = 'all' | AcquisitionOperatorStatus;
type SourceFilter = 'all' | AcquisitionOperatorSource;

const statusLabels: Record<StatusFilter, string> = {
  all: 'All statuses',
  new: 'New',
  reviewed: 'Reviewed',
  contacted: 'Contacted',
  dismissed: 'Dismissed',
};

const statusClass: Record<AcquisitionOperatorStatus, string> = {
  new: 'border-blue-500/40 bg-blue-500/10 text-blue-300',
  reviewed: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  contacted: 'border-green-500/40 bg-green-500/10 text-green-300',
  dismissed: 'border-muted bg-muted/30 text-muted-foreground',
};

const sourceLabel: Record<AcquisitionOperatorSource, string> = {
  'social-signal-consumer': 'Social signal',
  'engager-harvester': 'Engager',
};

const eligibilityLabel: Record<AcquisitionManualOutreachEligibility, string> = {
  ELIGIBLE: 'Outreach eligible',
  PRIVACY_BLOCKED: 'Privacy blocked',
  ALREADY_WARM: 'Already warm',
};

const eligibilityClass: Record<AcquisitionManualOutreachEligibility, string> = {
  ELIGIBLE: 'border-green-500/40 bg-green-500/10 text-green-300',
  PRIVACY_BLOCKED: 'border-red-500/40 bg-red-500/10 text-red-300',
  ALREADY_WARM: 'border-muted bg-muted/30 text-muted-foreground',
};

const blockedActionTitle = (eligibility: AcquisitionManualOutreachEligibility): string =>
  eligibility === 'PRIVACY_BLOCKED'
    ? 'Manual outreach blocked by current privacy state'
    : 'Manual outreach blocked because the candidate is already warm';

const relativeTime = (value: string): string => {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'Unknown time';
  return formatDistanceToNow(date, { addSuffix: true });
};

export default function AcquisitionSignals(): React.ReactElement {
  useDocumentTitle('High-Intent Signals — Tacit');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [editing, setEditing] = useState<AcquisitionOperatorSignalWithEligibility | null>(null);
  const [editStatus, setEditStatus] = useState<AcquisitionOperatorStatus>('reviewed');
  const [editNote, setEditNote] = useState('');
  const [revealingId, setRevealingId] = useState<string | null>(null);

  const signalsQuery = useAcquisitionSignals({
    status: statusFilter === 'all' ? undefined : statusFilter,
    source: sourceFilter === 'all' ? undefined : sourceFilter,
    limit: 100,
  });
  const updateSignal = useUpdateAcquisitionSignal();
  const signals = signalsQuery.data?.signals ?? [];

  const counts = useMemo(() => ({
    total: signals.length,
    new: signals.filter((signal) => signal.status === 'new').length,
    contacted: signals.filter((signal) => signal.status === 'contacted').length,
    high: signals.filter((signal) => signal.highIntentScore >= 90).length,
  }), [signals]);

  const openEditor = (
    signal: AcquisitionOperatorSignalWithEligibility,
    status = signal.status,
  ): void => {
    setEditing(signal);
    setEditStatus(status);
    setEditNote(signal.note ?? '');
  };

  const saveEditor = async (): Promise<void> => {
    if (!editing) return;
    if (
      editing.eligibility !== 'ELIGIBLE' &&
      editStatus === 'contacted'
    ) {
      toast.error(blockedActionTitle(editing.eligibility));
      return;
    }
    try {
      await updateSignal.mutateAsync({
        id: editing.id,
        status: editStatus,
        note: editNote.trim() || null,
      });
      toast.success('Operator review saved');
      setEditing(null);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? 'We could not save this review. Try again.'
          : 'We could not save this review. Try again.',
      );
    }
  };

  const quickStatus = async (
    signal: AcquisitionOperatorSignalWithEligibility,
    status: AcquisitionOperatorStatus,
  ): Promise<void> => {
    if (status === 'contacted' && signal.eligibility !== 'ELIGIBLE') {
      toast.error(blockedActionTitle(signal.eligibility));
      return;
    }
    try {
      await updateSignal.mutateAsync({ id: signal.id, status, note: signal.note });
      toast.success(
        status === 'contacted'
          ? 'Manual outreach recorded'
          : 'Operator status saved',
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? 'We could not update this signal. Try again.'
          : 'We could not update this signal. Try again.',
      );
    }
  };

  const authorizeLinkedInAction = async (
    signal: AcquisitionOperatorSignalWithEligibility,
    action: 'copy' | 'open',
  ): Promise<void> => {
    if (signal.eligibility !== 'ELIGIBLE' || revealingId !== null) return;
    const pendingWindow = action === 'open' ? window.open('about:blank', '_blank') : null;
    if (pendingWindow) pendingWindow.opener = null;
    setRevealingId(signal.id);
    try {
      const revealed = await revealAcquisitionSignal(signal.id);
      if (action === 'copy') {
        await navigator.clipboard.writeText(revealed.linkedinUrl);
        toast.success('LinkedIn URL copied after a fresh privacy check');
      } else if (pendingWindow) {
        pendingWindow.location.replace(revealed.linkedinUrl);
      } else {
        toast.error('Your browser blocked the new tab. Use Copy LinkedIn URL instead.');
      }
    } catch (error) {
      pendingWindow?.close();
      toast.error(
        error instanceof Error
          ? 'LinkedIn access could not be authorized. Refresh and try again.'
          : 'LinkedIn access could not be authorized. Refresh and try again.',
      );
      await signalsQuery.refetch();
    } finally {
      setRevealingId(null);
    }
  };

  return (
    <Layout>
      <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
        <div className="mx-auto max-w-7xl space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <Radar className="h-6 w-6 text-primary" />
                <h1 className="text-2xl font-semibold tracking-tight">High-Intent Signals</h1>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                Privacy-safe candidates for operator review and manual LinkedIn outreach,
                ranked by intent score.
              </p>
            </div>
            <Button
              variant="outline"
              className="min-h-11 md:min-h-11"
              onClick={() => void signalsQuery.refetch()}
              disabled={signalsQuery.isFetching}
            >
              <RefreshCw className={cn('mr-2 h-4 w-4', signalsQuery.isFetching && 'animate-spin')} />
              Refresh signals
            </Button>
          </div>

          <Card className="border-green-500/30 bg-green-500/5">
            <CardContent className="flex items-start gap-3 p-4">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-green-400" />
              <div>
                <p className="text-sm font-medium text-green-300">Manual outreach boundary</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Tacit does not send messages or create CRM records here. “Contacted” only
                  records manual outreach already completed outside Tacit.
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ['Visible', counts.total],
              ['New', counts.new],
              ['90+ intent score', counts.high],
              ['Contacted', counts.contacted],
            ].map(([label, value]) => (
              <Card key={label}>
                <CardContent className="p-4">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
                  <p className="mt-1 text-2xl font-semibold">{value}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle className="text-base">Operator queue</CardTitle>
                <div className="flex flex-wrap gap-2">
                  <select
                    aria-label="Status filter"
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
                    className="h-11 rounded-md border border-input bg-background px-3 text-sm"
                  >
                    {Object.entries(statusLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                  <select
                    aria-label="Source filter"
                    value={sourceFilter}
                    onChange={(event) => setSourceFilter(event.target.value as SourceFilter)}
                    className="h-11 rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="all">All sources</option>
                    <option value="social-signal-consumer">Social signal</option>
                    <option value="engager-harvester">Engager</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <span id="intent-signal-copy-action-label" className="sr-only">
                Copy LinkedIn URL for
              </span>
              <span id="intent-signal-open-action-label" className="sr-only">
                Open LinkedIn for
              </span>
              <span id="intent-signal-contact-action-label" className="sr-only">
                Mark as contacted manually
              </span>
              <span id="intent-signal-review-action-label" className="sr-only">
                Review
              </span>
              {signalsQuery.isLoading ? (
                <div
                  className="space-y-2 p-4"
                  role="status"
                  aria-live="polite"
                  aria-busy="true"
                >
                  <span className="sr-only">Loading intent signals</span>
                  {[1, 2, 3, 4].map((item) => <Skeleton key={item} className="h-16 w-full" />)}
                </div>
              ) : signalsQuery.isError ? (
                <div className="p-8 text-center" role="alert" aria-live="assertive">
                  <XCircle className="mx-auto h-8 w-8 text-red-400" />
                  <p className="mt-3 font-medium">Intent signals are unavailable</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    We could not load this queue. Use Refresh signals to try again.
                  </p>
                </div>
              ) : signals.length === 0 ? (
                <div className="p-10 text-center" role="status" aria-live="polite">
                  <Radar className="mx-auto h-9 w-9 text-muted-foreground" />
                  <p className="mt-3 font-medium">No candidates match these filters</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Candidates appear after a successful signal scan. Automatic retries are disabled.
                  </p>
                </div>
              ) : (
                <Table
                  className="block lg:table"
                  aria-label="Intent signal candidates"
                >
                  <TableHeader className="hidden lg:table-header-group">
                    <TableRow>
                      <TableHead>Candidate</TableHead>
                      <TableHead>Signal</TableHead>
                      <TableHead>Intent score</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Detected</TableHead>
                      <TableHead className="text-right">Manual actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="block lg:table-row-group">
                    {signals.map((signal) => (
                      <TableRow
                        key={signal.candidateKey}
                        className="grid gap-4 p-4 lg:table-row lg:p-0"
                      >
                        <TableCell
                          data-label="Candidate"
                          className="grid min-w-0 grid-cols-[6.5rem_minmax(0,1fr)] gap-2 p-0 before:text-xs before:font-medium before:text-muted-foreground before:content-[attr(data-label)] lg:table-cell lg:p-2 lg:before:hidden"
                        >
                          <span className="sr-only lg:hidden">Candidate: </span>
                          <div className="min-w-0">
                            <p
                              id={`intent-signal-candidate-${signal.id}`}
                              className="break-words font-medium"
                              data-content-origin="external"
                            >
                              {signal.fullName}
                            </p>
                            <p
                              className="break-words text-xs text-muted-foreground lg:max-w-64"
                              data-content-origin="external"
                            >
                              {[signal.jobTitle, signal.companyName].filter(Boolean).join(' · ') || '—'}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell
                          data-label="Signal"
                          className="grid min-w-0 grid-cols-[6.5rem_minmax(0,1fr)] gap-2 p-0 before:text-xs before:font-medium before:text-muted-foreground before:content-[attr(data-label)] lg:table-cell lg:p-2 lg:before:hidden"
                        >
                          <span className="sr-only lg:hidden">Signal: </span>
                          <div className="min-w-0">
                            <Badge variant="outline">{sourceLabel[signal.source]}</Badge>
                            <p
                              className="mt-1 break-words text-xs text-muted-foreground lg:max-w-48"
                              data-content-origin="domain"
                            >
                              {signal.signalType || signal.searchName || 'qualified candidate'}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell
                          data-label="Intent score"
                          className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-2 p-0 before:text-xs before:font-medium before:text-muted-foreground before:content-[attr(data-label)] lg:table-cell lg:p-2 lg:before:hidden"
                        >
                          <span className="sr-only lg:hidden">Intent score: </span>
                          <div>
                            <Badge
                              variant="outline"
                              className={signal.highIntentScore >= 90
                                ? 'border-red-500/40 bg-red-500/10 text-red-300'
                                : 'border-amber-500/40 bg-amber-500/10 text-amber-300'}
                            >
                              {signal.highIntentScore}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell
                          data-label="Status"
                          className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-2 p-0 before:text-xs before:font-medium before:text-muted-foreground before:content-[attr(data-label)] lg:table-cell lg:p-2 lg:before:hidden"
                        >
                          <span className="sr-only lg:hidden">Status: </span>
                          <div>
                            <Badge variant="outline" className={statusClass[signal.status]}>
                              {statusLabels[signal.status]}
                            </Badge>
                            <div className="mt-1">
                              <Badge
                                variant="outline"
                                className={eligibilityClass[signal.eligibility]}
                              >
                                {eligibilityLabel[signal.eligibility]}
                              </Badge>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell
                          data-label="Detected"
                          className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-2 whitespace-nowrap p-0 text-xs text-muted-foreground before:text-xs before:font-medium before:text-muted-foreground before:content-[attr(data-label)] lg:table-cell lg:p-2 lg:before:hidden"
                        >
                          <span className="sr-only lg:hidden">Detected: </span>
                          <div>
                            {relativeTime(signal.signalDetectedAt)}
                            <p className="mt-1 text-[11px]">
                              Privacy checked {relativeTime(signal.privacyCheckedAt)}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell
                          data-label="Manual actions"
                          className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-2 p-0 before:text-xs before:font-medium before:text-muted-foreground before:content-[attr(data-label)] lg:table-cell lg:p-2 lg:before:hidden"
                        >
                          <span className="sr-only lg:hidden">Manual actions: </span>
                          <div className="flex flex-wrap justify-start gap-2 lg:justify-end lg:gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="min-h-11 min-w-11 md:min-h-11 md:min-w-11"
                              aria-label="Copy LinkedIn URL"
                              aria-labelledby={`intent-signal-copy-action-label intent-signal-candidate-${signal.id}`}
                              title={signal.eligibility === 'ELIGIBLE'
                                ? 'Copy LinkedIn URL'
                                : blockedActionTitle(signal.eligibility)}
                              disabled={signal.eligibility !== 'ELIGIBLE' || revealingId !== null}
                              onClick={() => void authorizeLinkedInAction(signal, 'copy')}
                            >
                              <Clipboard className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="min-h-11 min-w-11 md:min-h-11 md:min-w-11"
                              aria-label="Open LinkedIn"
                              aria-labelledby={`intent-signal-open-action-label intent-signal-candidate-${signal.id}`}
                              title={signal.eligibility === 'ELIGIBLE'
                                ? 'Open LinkedIn after fresh privacy check'
                                : blockedActionTitle(signal.eligibility)}
                              disabled={signal.eligibility !== 'ELIGIBLE' || revealingId !== null}
                              onClick={() => void authorizeLinkedInAction(signal, 'open')}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                            {signal.status !== 'contacted' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="min-h-11 min-w-11 md:min-h-11 md:min-w-11"
                                aria-label="Mark as contacted manually"
                                aria-labelledby={`intent-signal-contact-action-label intent-signal-candidate-${signal.id}`}
                                title={signal.eligibility === 'ELIGIBLE'
                                  ? 'Mark manual outreach as contacted'
                                  : blockedActionTitle(signal.eligibility)}
                                disabled={
                                  updateSignal.isPending || signal.eligibility !== 'ELIGIBLE'
                                }
                                onClick={() => void quickStatus(signal, 'contacted')}
                              >
                                <CheckCircle2 className={cn(
                                  'h-4 w-4',
                                  signal.eligibility === 'ELIGIBLE'
                                    ? 'text-green-400'
                                    : 'text-muted-foreground',
                                )} />
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              className="min-h-11 md:min-h-11"
                              aria-labelledby={`intent-signal-review-action-label intent-signal-candidate-${signal.id}`}
                              onClick={() => openEditor(signal)}
                            >
                              Review
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Operator review</DialogTitle>
            <DialogDescription>
              Record an operator review or manual outreach. Tacit does not send a message or
              create a CRM record from this dialog.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <p
                  className="break-words font-medium"
                  data-content-origin="external"
                >
                  {editing.fullName}
                </p>
                <p
                  className="break-words text-sm text-muted-foreground"
                  data-content-origin="external"
                >
                  {[editing.jobTitle, editing.companyName].filter(Boolean).join(' · ')}
                </p>
              </div>
              <label className="block space-y-1 text-sm">
                <span>Status</span>
                <select
                  value={editStatus}
                  onChange={(event) => setEditStatus(event.target.value as AcquisitionOperatorStatus)}
                  className="h-11 w-full rounded-md border border-input bg-background px-3"
                >
                  <option value="new">New</option>
                  <option value="reviewed">Reviewed</option>
                  <option
                    value="contacted"
                    disabled={editing.eligibility !== 'ELIGIBLE'}
                  >
                    Contacted manually
                  </option>
                  <option value="dismissed">Dismissed</option>
                </select>
              </label>
              <label className="block space-y-1 text-sm">
                <span>Operator note</span>
                <Textarea
                  value={editNote}
                  maxLength={2000}
                  rows={5}
                  onChange={(event) => setEditNote(event.target.value)}
                  placeholder="Add review context or note manual outreach completed outside Tacit."
                  data-content-origin="user"
                />
              </label>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  className="min-h-11 md:min-h-11"
                  onClick={() => setEditing(null)}
                >
                  Cancel
                </Button>
                <Button
                  className="min-h-11 md:min-h-11"
                  onClick={() => void saveEditor()}
                  disabled={
                    updateSignal.isPending ||
                    (
                      editing.eligibility !== 'ELIGIBLE' &&
                      editStatus === 'contacted'
                    )
                  }
                >
                  {updateSignal.isPending ? 'Saving…' : 'Save review'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
